import React, { useEffect, useMemo, useState } from 'react';
import {
  Input, TextArea, Button, SizeTag, SectionContainer, FormSectionHeader,
  FormGrid, SizingGrid, Field, FieldLabel, FieldError, FieldHint, ControlRow,
} from '../styles/CustomerSpecsIndexStyles';
import TypeaheadSelect from './TypeaheadSelect';
import SegmentedToggle from './SegmentedToggle';
import ManualUpcField from './ManualUpcField';
import SizingMultiSelect from './SizingMultiSelect';
import TieredGradeList from './TieredGradeList';
import { gradeColor } from '../utils/gradeColor';
import { NO_PLU_LABEL, NO_PLU_DISPLAY, labelForUpcSelection } from '../utils/customerSpecUpc';
import { caseTagOptionsForCustomer } from '../utils/caseTagScope';
import { visibleLabels, matches, autoDefault, canonicalCustomerId } from '../utils/bagLabelRules';

// Shared Customer Specs field body — the five sections + all pickers. Rendered by BOTH the
// full-page Entry Form and the Manage edit modal so the two are identical by construction
// (form-ux plan, M1). Controlled: the parent owns the state (`form`/`setForm`), the reference
// data + derived option memos (`refs`), submit, and the dirty guard; this component only renders
// the fields and writes back via `setForm`. The DC-Specific/All-Destinations scope toggle stays
// in each parent's chrome (header / modal header), so field order is unchanged on both surfaces.
//
// Props:
//   mode        'create' | 'edit'  — Customer is a picker in create, read-only in edit
//   form        the spec state object (formData / panelData)
//   setForm     its setter (setFormData / setPanelData)
//   refs        { customers, commodities, styles, labels, methods, methodOptions, tihi, caseTags,
//                 pluMap, pluImagesByLabelId, destinationOptions, sizeOptionsForCommodity,
//                 varietyOptionsForCommodity, gradeOptionsForCommodity, derivedPLUIds,
//                 mergedUpcOptions, upcOptionByValue, tihiById, bagLabels }
//   fieldErrors { CustomerID?, DomesticOrImport?, CommodityID?, BagLabelTieStyleIds? }
//   onLightbox  (src) => void — open the image lightbox

const PALLET_OPTIONS = ['Chep', 'Regular', 'Export'];
const YES_NO = ['Yes', 'No'];
const DOMESTIC_IMPORT = ['Domestic', 'Import'];
const BWC_TYPES = ['BULK', 'Weight', 'Count'];
const TEMP_RECORDER_OPTIONS = [
  'DeltaTRack Mini Recorder (DTMINI)',
  'Aldi Temp Tale Geo (ALDITTG)',
  'Copeland Temp Recorder (COPE)',
  'DeltaTRack Recorder (DT)',
  'Export Recorder (ER)',
  'Loblaw Recorder (LR)',
  'Locus Traxx Temp Recorder (LT)',
  'Temperature Recorder (R)',
  'TopCo Tive Recorder (T)',
  'RF Temp Recorder (RF)',
  'Temp Tale Direct Recorder (TD)',
  'Temp Tale Recorder (TT)',
  'Temp Tale Geo (TTG)',
  'Temp Trip (TTRIP)',
  'Yellow Temp Tale Recorder (YT)',
];

const REQUIRED_MARK = <span style={{ color: 'var(--color-danger)' }} aria-hidden="true">*</span>;

// A removable chip rendered as a real <button> so it is keyboard-operable (Enter/Space
// fire onClick; buttons are focusable). Non-removable display chips stay a plain SizeTag.
const RemovableTag = ({ label, onRemove, background, disabled }) => (
  <SizeTag
    as="button"
    type="button"
    aria-label={`Remove ${label}`}
    onClick={disabled ? undefined : onRemove}
    style={{ cursor: disabled ? 'default' : 'pointer', background }}
  >
    {label}
  </SizeTag>
);

const SpecFormFields = ({ mode = 'create', form, setForm, refs = {}, fieldErrors = {}, onLightbox }) => {
  const {
    customers = [], commodities = [], styles = [], labels = [], methods = [], methodOptions = [],
    tihi = [], caseTags = [], pluMap = {}, pluImagesByLabelId = {},
    destinationOptions = [], sizeOptionsForCommodity = [], varietyOptionsForCommodity = [],
    gradeOptionsForCommodity = [], derivedPLUIds = [], mergedUpcOptions = [], upcOptionByValue = new Map(),
    tihiById = {}, bagLabels = [],
  } = refs;

  const patch = (partial) => setForm((prev) => ({ ...prev, ...partial }));
  const handleChange = (e) => { const { name, value } = e.target; setForm((prev) => ({ ...prev, [name]: value })); };

  // Temperature Recorder: the select shows a preset or "Other"; the free-text box appears for Other.
  // Local UI state synced from the persisted TemperatureRecorder value (self-contained to this field).
  const [temperatureRecorderSelect, setTemperatureRecorderSelect] = useState('');
  useEffect(() => {
    const val = form.TemperatureRecorder || '';
    if (!val) { setTemperatureRecorderSelect(''); return; }
    setTemperatureRecorderSelect(TEMP_RECORDER_OPTIONS.includes(val) ? val : 'Other');
  }, [form.TemperatureRecorder]);

  const caseTagOptions = useMemo(
    () => caseTagOptionsForCustomer(caseTags, form.CustomerID, form.CaseTagID),
    [caseTags, form.CustomerID, form.CaseTagID]
  );

  // Bag-label context for the Styles tier. Customer id must be the canonical dropdown
  // id (create-mode CustomerID already is; edit resolves by CustomerName — R3).
  const bagCustomerId = useMemo(
    () => canonicalCustomerId(customers, { CustomerName: form.CustomerName, CustomerID: form.CustomerID }),
    [customers, form.CustomerName, form.CustomerID]
  );
  const bagVisible = useMemo(() => visibleLabels(bagLabels, bagCustomerId), [bagLabels, bagCustomerId]);
  const styleRowById = useMemo(() => {
    const m = new Map();
    (styles || []).forEach((s) => m.set(Number(s.UniqueID), s));
    return m;
  }, [styles]);

  // Commodity is a spec-level axis of the bag-label rules (Customers > Commodity >
  // Type > Style), so it feeds every match check alongside the style row.
  const bagCommodityId = useMemo(() => {
    const n = Number(form.CommodityID);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [form.CommodityID]);

  // Styles flagged by the save-time bag-tie check, outlined until one is picked.
  const bagTieStyleIds = useMemo(
    () => new Set((fieldErrors.BagLabelTieStyleIds || []).map(Number)),
    [fieldErrors.BagLabelTieStyleIds]
  );

  const bagLabelContextFor = (styleId) => {
    const styleRow = styleRowById.get(Number(styleId)) || { UniqueID: Number(styleId), Type: null };
    const entry = (form.StyleIDs || []).find((e) => Number(e && typeof e === 'object' ? e.value : e) === Number(styleId));
    const selection = entry && typeof entry === 'object' ? (entry.bagLabels ?? null) : null;
    // Only rule-matching labels are offerable (supersedes the original "all visible
    // labels are checkable" decision). An already-pinned label that no longer matches
    // stays listed so it can be unchecked — filtering it out would leave the pin
    // applied with no control to clear it.
    const pinned = new Set(Array.isArray(selection) ? selection.map(Number) : []);
    const options = bagVisible.reduce((acc, l) => {
      const matched = matches(l, styleRow, bagCommodityId);
      if (matched || pinned.has(Number(l.UniqueID))) acc.push({ label: l, matched });
      return acc;
    }, []);
    return {
      options,
      autoPick: autoDefault(bagVisible, styleRow, bagCommodityId),
      selection,
      invalid: bagTieStyleIds.has(Number(styleId)),
    };
  };

  const setStyleBagLabels = (styleId, nextWire) => patch({
    StyleIDs: (form.StyleIDs || []).map((e) => (
      Number(e && typeof e === 'object' ? e.value : e) === Number(styleId)
        ? { ...(e && typeof e === 'object' ? e : { value: e, grades: [] }), bagLabels: nextWire }
        : e
    )),
  });

  return (
    <>
      <SectionContainer>
        <FormSectionHeader>Customer &amp; Shipment</FormSectionHeader>
        <FormGrid>
          <Field>
            <FieldLabel>Customer {mode === 'create' ? REQUIRED_MARK : null}</FieldLabel>
            {mode === 'edit' ? (
              <div style={{ padding: '8px 10px', background: 'var(--shadow-a05)', borderRadius: 8 }}>
                {form.CustomerName || '—'}
              </div>
            ) : (
              <TypeaheadSelect
                ariaLabel="Customer"
                ariaRequired
                ariaInvalid={!!fieldErrors.CustomerID}
                value={form.CustomerID}
                placeholder="Select customer"
                size="md"
                options={(customers || []).map((c) => ({ value: String(c.UniqueID), label: String(c.Customer || '') }))}
                onChange={(val) => patch({ CustomerID: String(val || '') })}
              />
            )}
            {fieldErrors.CustomerID ? <FieldError>Customer is required.</FieldError> : null}
          </Field>

          <Field>
            <FieldLabel>Domestic or Import {REQUIRED_MARK}</FieldLabel>
            <TypeaheadSelect
              ariaLabel="Domestic or Import"
              ariaRequired
              ariaInvalid={!!fieldErrors.DomesticOrImport}
              value={form.DomesticOrImport}
              placeholder="Select..."
              size="sm"
              options={DOMESTIC_IMPORT.map((v) => ({ value: v, label: v }))}
              onChange={(val) => patch({ DomesticOrImport: String(val || '') })}
            />
            {fieldErrors.DomesticOrImport ? <FieldError>Please select Domestic or Import.</FieldError> : null}
          </Field>

          <Field style={{ gridColumn: '1 / -1' }}>
            <FieldLabel>Destinations</FieldLabel>
            <ControlRow>
              <TypeaheadSelect
                ariaLabel="Add destination"
                value=""
                disabled={form.Universal}
                placeholder="Add destination..."
                size="md"
                options={[
                  ...(destinationOptions.length > 3 && !(form.Destinations || []).includes('__ALL__') ? [{ value: '__ALL__', label: 'All Destinations' }] : []),
                  ...destinationOptions
                    .filter((d) => !(form.Destinations || []).includes(d) && !((form.Destinations || []).includes('__ALL__')))
                    .map((d) => ({ value: d, label: d })),
                ]}
                onChange={(val) => {
                  const v = String(val || '').trim();
                  if (!v || form.Universal) return;
                  patch({ Destinations: v === '__ALL__' ? ['__ALL__'] : Array.from(new Set([...(form.Destinations || []), v])) });
                }}
              />
              <Button
                type="button"
                disabled={form.Universal}
                onClick={() => patch({ Destinations: ['__ALL__'] })}
              >
                Select All
              </Button>
            </ControlRow>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              {(form.Destinations || []).map((d) => (
                <RemovableTag
                  key={d}
                  label={d === '__ALL__' ? 'All Destinations' : d}
                  disabled={form.Universal}
                  onRemove={() => patch({ Destinations: (form.Destinations || []).filter((v) => v !== d) })}
                  background="var(--grey-600)"
                />
              ))}
            </div>
          </Field>
        </FormGrid>
      </SectionContainer>

      <SectionContainer>
        <FormSectionHeader>Product Details</FormSectionHeader>
        <FormGrid>
          <Field>
            <FieldLabel>Commodity {REQUIRED_MARK}</FieldLabel>
            <TypeaheadSelect
              ariaLabel="Commodity"
              ariaRequired
              ariaInvalid={!!fieldErrors.CommodityID}
              value={form.CommodityID}
              placeholder="Select commodity"
              size="md"
              options={(commodities || []).map((c) => ({ value: String(c.UniqueID), label: String(c.Commodity || '') }))}
              onChange={(val) => patch({ CommodityID: String(val || '') })}
            />
            {fieldErrors.CommodityID ? <FieldError>Commodity is required.</FieldError> : null}
          </Field>

          {!form.CommodityID && (
            <FieldHint style={{ gridColumn: '1 / -1' }}>
              Select a commodity to unlock varieties, grades, and sizing.
            </FieldHint>
          )}

          <Field style={{ gridColumn: 'span 2' }}>
            <FieldLabel>Varieties</FieldLabel>
            <ControlRow>
              <TypeaheadSelect
                ariaLabel="Add variety"
                value=""
                placeholder="Add variety..."
                size="md"
                disabled={!form.CommodityID}
                options={varietyOptionsForCommodity
                  .filter((v) => !(form.Varieties || []).includes(v))
                  .map((v) => ({ value: v, label: v }))}
                onChange={(val) => {
                  const v = String(val || '').trim();
                  if (!v) return;
                  patch({ Varieties: Array.from(new Set([...(form.Varieties || []), v])) });
                }}
              />
            </ControlRow>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(form.Varieties || []).map((v) => (
                <RemovableTag key={v} label={v} onRemove={() => patch({ Varieties: (form.Varieties || []).filter((x) => x !== v) })} background="var(--grey-600)" />
              ))}
            </div>
          </Field>

          <Field>
            <FieldLabel>Bulk / Weight / Count</FieldLabel>
            <ControlRow>
              <TypeaheadSelect
                ariaLabel="Bulk/Weight/Count"
                value={form.BWCType}
                placeholder="Select type"
                size="sm"
                options={BWC_TYPES.map((t) => ({ value: t, label: t }))}
                onChange={(val) => patch({ BWCType: String(val || '') })}
              />
              {(form.BWCType === 'Weight' || form.BWCType === 'Count') && (
                <Input
                  name="BWCValue"
                  type="number"
                  min="0"
                  step={form.BWCType === 'Weight' ? '0.01' : '1'}
                  value={form.BWCValue}
                  onChange={handleChange}
                  placeholder={form.BWCType === 'Weight' ? 'lb' : 'count'}
                  $size="sm"
                  style={{ width: 140 }}
                />
              )}
              <div style={{ color: 'var(--shadow-a65)', fontSize: '0.9rem' }}>{form.BWCDisplay}</div>
            </ControlRow>
          </Field>

          <Field style={{ gridColumn: 'span 2' }}>
            <FieldLabel>Grades</FieldLabel>
            <ControlRow>
              <TypeaheadSelect
                ariaLabel="Add grade"
                value=""
                placeholder="Add grade..."
                size="md"
                disabled={!form.CommodityID}
                options={gradeOptionsForCommodity
                  .filter((g) => !(form.Grades || []).includes(g))
                  .map((g) => ({ value: g, label: g }))}
                onChange={(val) => {
                  const v = String(val || '').trim();
                  if (!v) return;
                  patch({ Grades: Array.from(new Set([...(form.Grades || []), v])) });
                }}
              />
            </ControlRow>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(form.Grades || []).map((g) => (
                <RemovableTag key={g} label={g} onRemove={() => patch({ Grades: (form.Grades || []).filter((x) => x !== g) })} background={gradeColor(g).bg} />
              ))}
            </div>
          </Field>
        </FormGrid>
      </SectionContainer>

      <SectionContainer>
        <FormSectionHeader>Sizing</FormSectionHeader>
        <SizingGrid>
          <Field>
            <FieldLabel>Recommended Sizing</FieldLabel>
            <SizingMultiSelect
              ariaLabel="Add recommended size"
              placeholder="Add recommended size..."
              orderedSizes={sizeOptionsForCommodity}
              disabled={!form.CommodityID || sizeOptionsForCommodity.length === 0}
              value={form.RecommendedSizingList}
              onChange={(list) => patch({ RecommendedSizingList: list })}
            />
          </Field>

          <Field>
            <FieldLabel>Spec Size Range</FieldLabel>
            <SizingMultiSelect
              ariaLabel="Add spec size"
              placeholder="Add spec size..."
              orderedSizes={sizeOptionsForCommodity}
              disabled={!form.CommodityID || sizeOptionsForCommodity.length === 0}
              value={form.SpecSizeRangeList}
              onChange={(list) => patch({ SpecSizeRangeList: list })}
            />
          </Field>

          <Field>
            <FieldLabel>Subbing Options</FieldLabel>
            <SizingMultiSelect
              ariaLabel="Add subbing size"
              placeholder="Add subbing size..."
              sort={false}
              orderedSizes={sizeOptionsForCommodity}
              disabled={!form.CommodityID || sizeOptionsForCommodity.length === 0}
              value={form.SubbingOptions}
              onChange={(list) => patch({ SubbingOptions: list })}
            />
          </Field>

          <Field>
            <FieldLabel>Approval Needed</FieldLabel>
            <TypeaheadSelect
              ariaLabel="Approval Needed"
              value={form.ApprovalNeeded || ''}
              placeholder="Select..."
              size="sm"
              disabled={!form.CommodityID}
              options={YES_NO.map((v) => ({ value: v, label: v }))}
              onChange={(val) => patch({ ApprovalNeeded: String(val || '') })}
            />
          </Field>

          <Field style={{ gridColumn: '1 / -1' }}>
            <FieldLabel>Sizing Notes</FieldLabel>
            <TextArea
              name="SizingNotes"
              value={form.SizingNotes || ''}
              onChange={handleChange}
              placeholder="Enter sizing notes..."
              style={{ minHeight: 96 }}
            />
          </Field>
        </SizingGrid>
      </SectionContainer>

      <SectionContainer>
        <FormSectionHeader>IDs &amp; Codes</FormSectionHeader>
        <FormGrid>
          <Field style={{ gridColumn: '1 / -1' }}>
            <FieldLabel>Styles</FieldLabel>
            <TieredGradeList
              addAriaLabel="Add style"
              addPlaceholder="Add style..."
              gradeMode
              specGrades={form.Grades}
              value={form.StyleIDs}
              onChange={(next) => patch({ StyleIDs: next })}
              options={styles.map((s) => ({ value: String(s.UniqueID), label: String(s.Style || '') }))}
              coerce={Number}
              displayFor={(id) => styles.find((s) => Number(s.UniqueID) === Number(id))?.Style || id}
              bagLabelContextFor={bagLabelContextFor}
              onBagLabelsChange={setStyleBagLabels}
            />
            {bagTieStyleIds.size ? (
              <FieldError>Tied bag-label rules — select one bag label for the outlined style(s).</FieldError>
            ) : null}
          </Field>

          <Field style={{ gridColumn: '1 / -1' }}>
            <FieldLabel>Label ID</FieldLabel>
            <TieredGradeList
              addAriaLabel="Add label"
              addPlaceholder="Add label..."
              gradeMode
              specGrades={form.Grades}
              value={form.LabelIDs}
              onChange={(next) => patch({ LabelIDs: next })}
              options={labels.map((l) => ({ value: String(l), label: String(l) }))}
              coerce={String}
              displayFor={(id) => (id === NO_PLU_LABEL ? NO_PLU_DISPLAY : id)}
              pinnedValue={NO_PLU_LABEL}
              pinnedLabel={NO_PLU_DISPLAY}
              headerNote={`${NO_PLU_DISPLAY} means the selected grade(s) aren't tied to a label/PLU row; it can be combined with other Label IDs and derives no PLU.`}
              renderFooter={() => (
                <>
                  {/* PIDPLU images if LabelID belongs to PIDPLU (supports multiple) */}
                  {(() => {
                    const lbls = (form.LabelIDs || []).map((e) => (e && typeof e === 'object') ? e.value : e).filter((l) => l && l !== NO_PLU_LABEL && Boolean(pluMap[l]));
                    if (!lbls.length) return null;
                    const images = [];
                    lbls.forEach((l) => (pluImagesByLabelId[l] || []).forEach((src) => images.push({ l, src })));
                    const uniq = Array.from(new Map(images.map((i) => [i.src, i])).values());
                    if (!uniq.length) return null;
                    return (
                      <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                        {uniq.map((it, i) => (
                          <img
                            key={`${it.l}-${i}`}
                            src={it.src}
                            alt={`PLU ${it.l}`}
                            onClick={() => onLightbox?.(it.src)}
                            style={{ height: 60, borderRadius: 6, border: '1px solid var(--shadow-a08)', cursor: 'zoom-in' }}
                          />
                        ))}
                      </div>
                    );
                  })()}
                </>
              )}
            />
          </Field>

          <Field style={{ gridColumn: '1 / -1' }}>
            <FieldLabel>PLU (derived)</FieldLabel>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', color: 'var(--shadow-a55)' }}>
              {derivedPLUIds.length ? derivedPLUIds.map((id) => (
                <SizeTag key={id} style={{ background: 'var(--grey-500)' }}>{id}</SizeTag>
              )) : 'Derived from Label ID(s)'}
            </div>
          </Field>

          <Field style={{ gridColumn: '1 / -1' }}>
            <FieldLabel>Methods</FieldLabel>
            <TieredGradeList
              addAriaLabel="Add method"
              addPlaceholder="Add method..."
              gradeMode
              specGrades={form.Grades}
              value={form.MethodIDs}
              onChange={(next) => patch({ MethodIDs: next })}
              options={(methodOptions || []).map((m) => ({ value: String(m.UniqueID), label: String(m.MethodName || m.UniqueID) }))}
              coerce={Number}
              displayFor={(id) => methods.find((m) => Number(m.UniqueID) === Number(id))?.MethodName || id}
            />
          </Field>

          <Field style={{ gridColumn: '1 / -1' }}>
            <FieldLabel>UPC(s)</FieldLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <SegmentedToggle
                ariaLabel="UPC source"
                size="sm"
                value={form.upcMode || 'catalog'}
                onChange={(m) => patch({ upcMode: m })}
                options={[
                  { value: 'catalog', label: 'Catalog' },
                  { value: 'custom', label: 'Custom' },
                ]}
              />
              {(form.upcMode || 'catalog') === 'custom' ? (
                <>
                  <ManualUpcField
                    value={form.manualUpc}
                    onChange={(v) => patch({ manualUpc: v })}
                  />
                  {(form.upcSelectionKeys || []).length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <FieldHint>Catalog UPCs are set aside while a custom code is used.</FieldHint>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', opacity: 0.5, pointerEvents: 'none' }}>
                        {(form.upcSelectionKeys || []).map((key) => (
                          <SizeTag key={key} style={{ background: 'var(--grey-600)' }}>
                            {labelForUpcSelection(key, upcOptionByValue)}
                          </SizeTag>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <TypeaheadSelect
                    ariaLabel="Add UPC"
                    value=""
                    placeholder="Add UPC..."
                    size="lg"
                    options={mergedUpcOptions
                      .filter((o) => !(form.upcSelectionKeys || []).includes(o.value))
                      .map((o) => ({ value: o.value, label: o.label }))}
                    onChange={(val) => {
                      const key = String(val || '').trim();
                      if (!key) return;
                      patch({ upcSelectionKeys: Array.from(new Set([...(form.upcSelectionKeys || []), key])) });
                    }}
                  />
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {(form.upcSelectionKeys || []).map((key) => (
                      <RemovableTag
                        key={key}
                        label={labelForUpcSelection(key, upcOptionByValue)}
                        onRemove={() => patch({ upcSelectionKeys: (form.upcSelectionKeys || []).filter((k) => k !== key) })}
                        background="var(--grey-600)"
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </Field>
        </FormGrid>
      </SectionContainer>

      <SectionContainer>
        <FormSectionHeader>Notes &amp; Misc</FormSectionHeader>
        <FormGrid>
          <Field style={{ gridColumn: 'span 2' }}>
            <FieldLabel>Packaging Notes</FieldLabel>
            <TextArea name="PackagingNotes" value={form.PackagingNotes} onChange={handleChange} style={{ minHeight: 96 }} />
          </Field>
          <Field style={{ gridColumn: 'span 2' }}>
            <FieldLabel>Customer Item Number</FieldLabel>
            <Input name="CustomerItemNumber" value={form.CustomerItemNumber} onChange={handleChange} $size="md" />
          </Field>
          <Field style={{ gridColumn: 'span 2' }}>
            <FieldLabel>Quality Notes</FieldLabel>
            <TextArea name="QualityNotes" value={form.QualityNotes} onChange={handleChange} style={{ minHeight: 96 }} />
          </Field>
          <Field style={{ gridColumn: 'span 2' }}>
            <FieldLabel>GTIN</FieldLabel>
            <Input name="GTIN" value={form.GTIN} onChange={handleChange} $size="md" />
          </Field>

          <Field style={{ gridColumn: 'span 2' }}>
            <FieldLabel>Temperature Recorder</FieldLabel>
            <ControlRow>
              <TypeaheadSelect
                ariaLabel="Temperature Recorder"
                value={temperatureRecorderSelect || ''}
                placeholder="Select recorder..."
                size="lg"
                options={[
                  ...TEMP_RECORDER_OPTIONS.map((o) => ({ value: o, label: o })),
                  { value: 'Other', label: 'Other...' },
                ]}
                onChange={(val) => {
                  const sel = String(val || '');
                  setTemperatureRecorderSelect(sel);
                  if (sel && sel !== 'Other') {
                    patch({ TemperatureRecorder: sel });
                  }
                  if (sel === 'Other' && TEMP_RECORDER_OPTIONS.includes(form.TemperatureRecorder)) {
                    patch({ TemperatureRecorder: '' });
                  }
                }}
              />
              {temperatureRecorderSelect === 'Other' && (
                <Input
                  name="TemperatureRecorder"
                  value={form.TemperatureRecorder}
                  onChange={handleChange}
                  placeholder="Enter custom recorder"
                  $size="md"
                  style={{ flex: '1 1 320px' }}
                />
              )}
            </ControlRow>
          </Field>

          <Field style={{ gridColumn: 'span 2' }}>
            <FieldLabel>Pallet Type</FieldLabel>
            <ControlRow>
              <TypeaheadSelect
                ariaLabel="Add pallet type"
                value=""
                placeholder="Add pallet type..."
                size="md"
                options={PALLET_OPTIONS
                  .filter((p) => !(form.PalletTypes || []).includes(p))
                  .map((p) => ({ value: p, label: p }))}
                onChange={(val) => {
                  const v = String(val || '').trim();
                  if (!v) return;
                  patch({ PalletTypes: Array.from(new Set([...(form.PalletTypes || []), v])) });
                }}
              />
            </ControlRow>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(form.PalletTypes || []).map((p) => (
                <RemovableTag key={p} label={p} onRemove={() => patch({ PalletTypes: (form.PalletTypes || []).filter((x) => x !== p) })} background="var(--grey-600)" />
              ))}
            </div>
          </Field>

          <Field style={{ gridColumn: 'span 2' }}>
            <FieldLabel>Ti x Hi</FieldLabel>
            <ControlRow>
              <TypeaheadSelect
                ariaLabel="Add Ti x Hi"
                value=""
                placeholder="Add Ti x Hi..."
                size="md"
                options={tihi
                  .filter((t) => !(form.TiHiIDs || []).some((i) => Number(i) === Number(t.UniqueID)))
                  .map((t) => ({ value: String(t.UniqueID), label: String(t.TiHi || t.UniqueID) }))}
                onChange={(val) => {
                  const id = Number(val);
                  if (!Number.isFinite(id)) return;
                  patch({ TiHiIDs: Array.from(new Set([...(form.TiHiIDs || []), id])) });
                }}
              />
            </ControlRow>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(form.TiHiIDs || []).map((id) => (
                <RemovableTag key={id} label={String(tihiById[Number(id)] || id)} onRemove={() => patch({ TiHiIDs: (form.TiHiIDs || []).filter((v) => Number(v) !== Number(id)) })} background="var(--grey-600)" />
              ))}
            </div>
          </Field>

          <Field style={{ gridColumn: 'span 2' }}>
            <FieldLabel>Shipping Notes</FieldLabel>
            <TextArea name="ShippingNotes" value={form.ShippingNotes} onChange={handleChange} style={{ minHeight: 96 }} />
          </Field>

          <Field style={{ gridColumn: 'span 2' }}>
            <FieldLabel>Case Tag</FieldLabel>
            <TypeaheadSelect
              ariaLabel="Case Tag"
              value={form.CaseTagID}
              placeholder="Select case tag"
              size="lg"
              options={caseTagOptions}
              onChange={(val) => patch({ CaseTagID: String(val || '') })}
            />
          </Field>

          <Field style={{ gridColumn: '1 / -1' }}>
            <FieldLabel>Bag Label Notes</FieldLabel>
            <TextArea name="BagLabelNotes" value={form.BagLabelNotes} onChange={handleChange} style={{ minHeight: 96 }} />
          </Field>
        </FormGrid>
      </SectionContainer>
    </>
  );
};

export default SpecFormFields;
